/*
 * Collection faceted filters — instant AJAX on desktop, batched drawer on mobile.
 *
 * Progressive enhancement over snippets/collection-filters-sidebar.liquid + collection-toolbar.liquid:
 *   - With no JS the sidebar is a plain <form> GET, chips are plain links, sort is its own form.
 *   - With JS, desktop updates the grid in place (Section Rendering API, ?section_id=...), mobile
 *     opens an off-canvas drawer, batches changes behind a "Show X results" apply button, and only
 *     commits on apply.
 *
 * Uses event delegation on document so swapped-in DOM keeps working with no re-binding. After every
 * swap it re-initialises the Load More controller (window.PatinaLoadMore.init) against the fresh grid.
 */
(function () {
  'use strict';

  var SEL = {
    form: '[data-filters-form]',
    sortSelect: '[data-sort-select]',
    sidebar: '[data-collection-sidebar]',
    toolbar: '[data-collection-toolbar]',
    container: '[data-collection-container]',
    loadWrap: '[data-load-more-wrap]',
    results: '.collection-results',
    applyBtn: '[data-filters-apply]'
  };
  var DESKTOP = '(min-width: 760px)';
  var DEBOUNCE = 300;

  function isDesktop() { return !window.matchMedia || window.matchMedia(DESKTOP).matches; }
  function ready(fn) { if (document.readyState !== 'loading') fn(); else document.addEventListener('DOMContentLoaded', fn); }
  function sectionId() { var w = document.querySelector(SEL.loadWrap); return w ? w.getAttribute('data-section-id') : ''; }

  var debounceTimer = null;
  var docCache = {};

  /* Build the target path+query from the live controls (checked boxes, price inputs, sort). */
  function buildUrl() {
    var form = document.querySelector(SEL.form);
    var params = new URLSearchParams();
    if (form) {
      new FormData(form).forEach(function (value, key) {
        if (key === 'sort_by') return;               // sort comes from the select, added below
        if (value !== '' && value != null) params.append(key, value);
      });
    }
    var sort = document.querySelector(SEL.sortSelect);
    if (sort && sort.value) params.set('sort_by', sort.value);
    var qs = params.toString();
    return window.location.pathname + (qs ? '?' + qs : '');
  }

  function fetchDoc(url) {
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    var fetchUrl = url + sep + 'section_id=' + encodeURIComponent(sectionId());
    if (docCache[fetchUrl]) return Promise.resolve(docCache[fetchUrl]);
    return fetch(fetchUrl, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        docCache[fetchUrl] = doc;
        return doc;
      });
  }

  function setLoading(on) {
    var r = document.querySelector(SEL.results);
    if (r) r.classList.toggle('is-loading', !!on);
  }

  /* Preserve which filter groups are open + sidebar scroll across a sidebar re-render. */
  function captureSidebarState() {
    var open = {};
    document.querySelectorAll('.filter-group[id]').forEach(function (g) { open[g.id] = g.hasAttribute('open'); });
    var sb = document.querySelector(SEL.sidebar);
    return { open: open, scroll: sb ? sb.scrollTop : 0 };
  }
  function restoreSidebarState(state) {
    if (!state) return;
    document.querySelectorAll('.filter-group[id]').forEach(function (g) {
      if (Object.prototype.hasOwnProperty.call(state.open, g.id)) {
        if (state.open[g.id]) g.setAttribute('open', ''); else g.removeAttribute('open');
      }
    });
    var sb = document.querySelector(SEL.sidebar);
    if (sb) sb.scrollTop = state.scroll;
  }

  function swapFrom(doc) {
    var state = captureSidebarState();
    [SEL.sidebar, SEL.toolbar, SEL.container, SEL.loadWrap].forEach(function (s) {
      var next = doc.querySelector(s), curr = document.querySelector(s);
      if (next && curr) curr.replaceWith(next);
    });
    restoreSidebarState(state);
    initCollapsibles();
    if (window.PatinaLoadMore && window.PatinaLoadMore.init) window.PatinaLoadMore.init();
  }

  function pushState(url) {
    try { window.history.pushState({ patinaFilter: true }, '', url); } catch (e) {}
  }

  /* Apply the current control state (used by desktop change + mobile apply + sort). */
  function apply(closeDrawerAfter) {
    var url = buildUrl();
    if (closeDrawerAfter) closeDrawer();
    setLoading(true);
    fetchDoc(url)
      .then(function (doc) { pushState(url); swapFrom(doc); setLoading(false); })
      .catch(function () { window.location.href = url; });
  }

  /* Apply a ready-made href (chip removal, clear-all, per-group clear). */
  function applyHref(href) {
    var url = href.replace(window.location.origin, '');
    setLoading(true);
    fetchDoc(url)
      .then(function (doc) { pushState(url); swapFrom(doc); setLoading(false); })
      .catch(function () { window.location.href = href; });
  }

  /* Mobile: refresh only the apply-button count for the pending (unapplied) selection. */
  function refreshPendingCount() {
    var url = buildUrl();
    fetchDoc(url).then(function (doc) {
      var btn = document.querySelector(SEL.applyBtn), src = doc.querySelector(SEL.applyBtn);
      if (btn && src) btn.textContent = src.textContent;
    }).catch(function () {});
  }

  /* ---- Mobile drawer ---- */
  function backdrop() {
    var b = document.querySelector('.filters-backdrop');
    if (!b) {
      b = document.createElement('div');
      b.className = 'filters-backdrop';
      b.addEventListener('click', closeDrawer);
      document.body.appendChild(b);
    }
    return b;
  }
  function openDrawer() {
    var sb = document.querySelector(SEL.sidebar);
    if (!sb) return;
    backdrop().classList.add('is-open');
    sb.classList.add('is-open');
    document.body.classList.add('filters-open');
  }
  function closeDrawer() {
    var sb = document.querySelector(SEL.sidebar);
    if (sb) sb.classList.remove('is-open');
    var b = document.querySelector('.filters-backdrop');
    if (b) b.classList.remove('is-open');
    document.body.classList.remove('filters-open');
  }

  /* ---- Show more ---- */
  function initCollapsibles() {
    document.querySelectorAll('[data-collapsible]').forEach(function (ul) {
      ul.classList.add('is-collapsed');
      var btn = ul.parentElement && ul.parentElement.querySelector('[data-show-more]');
      if (btn) {
        btn.setAttribute('aria-expanded', 'false');
        var t = btn.querySelector('[data-show-more-text]');
        if (t && btn.getAttribute('data-label-more')) t.textContent = btn.getAttribute('data-label-more');
      }
    });
  }
  function toggleShowMore(btn) {
    var body = btn.closest('.filter-group__body');
    var ul = body && body.querySelector('[data-collapsible]');
    if (!ul) return;
    var collapsed = ul.classList.toggle('is-collapsed');
    btn.setAttribute('aria-expanded', String(!collapsed));
    var t = btn.querySelector('[data-show-more-text]');
    if (t) t.textContent = collapsed ? (btn.getAttribute('data-label-more') || t.textContent) : (btn.getAttribute('data-label-less') || t.textContent);
  }

  /* ---- Delegated events ---- */
  function onChange(e) {
    var t = e.target;
    if (!t || !t.matches || !t.closest) return;
    if (t.matches(SEL.sortSelect)) { apply(false); return; }
    if (t.closest(SEL.form)) {
      if (isDesktop()) {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(function () { apply(false); }, DEBOUNCE);
      } else {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(refreshPendingCount, DEBOUNCE);
      }
    }
  }

  function onClick(e) {
    if (!e.target || !e.target.closest) return;
    var open = e.target.closest('[data-filters-open]');
    if (open) { e.preventDefault(); openDrawer(); return; }
    var close = e.target.closest('[data-filters-close]');
    if (close) { e.preventDefault(); closeDrawer(); return; }
    var more = e.target.closest('[data-show-more]');
    if (more) { e.preventDefault(); toggleShowMore(more); return; }
    var link = e.target.closest('[data-filter-link]');
    if (link) { e.preventDefault(); applyHref(link.getAttribute('href')); return; }
  }

  function onSubmit(e) {
    var form = e.target;
    if (form.matches(SEL.form)) { e.preventDefault(); apply(!isDesktop()); return; }   // mobile apply / Enter in price
    if (form.matches('[data-sort-form]')) { e.preventDefault(); apply(false); return; }
  }

  // When a filter group is opened, scroll its options into view so groups near the bottom of the
  // sidebar don't leave their options below the fold. `toggle` doesn't bubble → capture phase.
  function onToggle(e) {
    var g = e.target;
    if (!g || !g.matches || !g.matches('.filter-group') || !g.open) return;
    requestAnimationFrame(function () {
      if (g.scrollIntoView) g.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }

  window.addEventListener('popstate', function () {
    var url = window.location.pathname + window.location.search;
    setLoading(true);
    fetchDoc(url).then(function (doc) { swapFrom(doc); setLoading(false); }).catch(function () { window.location.reload(); });
  });

  ready(function () {
    document.addEventListener('change', onChange);
    document.addEventListener('click', onClick);
    document.addEventListener('submit', onSubmit);
    document.addEventListener('toggle', onToggle, true);
    initCollapsibles();
  });
})();
